import { useLayoutEffect, useRef, useState } from "react";
import EmptyState from "@/components/EmptyState";
import Radio from "@/components/Radio";
import SectionHeading from "@/components/SectionHeading";
import type { ExperienceIndexCatalogCompetency } from "../queries";
import type { CareerPlanAssessmentAnswer } from "./careerPlanHistory";
import {
  competencyAnswersPayload,
  experienceIndexSelectionsEqual,
  getExperienceIndexAssessmentView,
  hasLiveCatalogQuestions,
  liveCatalogCompetency,
  liveCatalogSelectionBlocked,
  selectedOptionIdsByQuestion
} from "./experienceIndexAnswers";

function experienceIndexHeadingBody({
  isEditing,
  isFocused,
  showEdit,
  hasSelections
}: {
  isEditing: boolean;
  isFocused: boolean;
  showEdit: boolean;
  hasSelections: boolean;
}) {
  if (isEditing) {
    return "Choose an answer for each question, then Save or Cancel.";
  }

  const choiceLine = hasSelections
    ? "Each question lists every choice. The selected choice is this person's answer."
    : "Each question lists every choice. This person has not answered these questions yet.";

  if (!isFocused) {
    return `${choiceLine} Historical snapshots do not include answers.`;
  }

  return showEdit ? choiceLine : `${choiceLine} You cannot change answers here.`;
}

export default function ExperienceIndexAnswersTab({
  answers,
  canEdit = false,
  competencies = [],
  focusedCompetencyId,
  isSaving = false,
  onSave
}: {
  answers: CareerPlanAssessmentAnswer[];
  canEdit?: boolean;
  competencies?: ExperienceIndexCatalogCompetency[];
  focusedCompetencyId?: string | null;
  isSaving?: boolean;
  onSave?: (competencyId: string, answers: { questionId: string; optionId: string }[]) => Promise<boolean>;
}) {
  const actionFocusRef = useRef<"first-radio" | "edit" | null>(null);
  const firstRadioOptionIdRef = useRef<string | null>(null);
  const groups = getExperienceIndexAssessmentView(competencies, answers, focusedCompetencyId);
  const focusedCatalog = liveCatalogCompetency(competencies, focusedCompetencyId);
  const focusedCompetencyName = groups[0]?.competencyName;
  const isFocused = Boolean(focusedCompetencyId);
  const isEmptyFocus = isFocused && groups.length === 0;
  const catalogSelections = selectedOptionIdsByQuestion(groups);
  const showEdit = Boolean(canEdit && isFocused && onSave && hasLiveCatalogQuestions(focusedCatalog));
  const [isEditing, setIsEditing] = useState(false);
  const [draftSelections, setDraftSelections] = useState<Record<string, string>>({});
  const [sessionCompetencyId, setSessionCompetencyId] = useState(focusedCompetencyId);

  if (focusedCompetencyId !== sessionCompetencyId) {
    setSessionCompetencyId(focusedCompetencyId);
    setIsEditing(false);
    setDraftSelections({});
  }

  useLayoutEffect(() => {
    const actionFocus = actionFocusRef.current;
    if (!actionFocus) return;
    actionFocusRef.current = null;

    if (actionFocus === "first-radio") {
      const optionId = firstRadioOptionIdRef.current;
      firstRadioOptionIdRef.current = null;
      const radio = optionId ? document.getElementById(`career-plan-ei-option-${optionId}`) : null;
      radio?.focus();
      return;
    }

    document.querySelector<HTMLElement>('[data-testid="career-plan-ei-answers-edit"]')?.focus();
  }, [isEditing]);

  if (!isFocused && answers.length === 0) {
    return (
      <EmptyState
        title="No Experience Index Answers"
        iconName="quiz"
        caption="This person has not answered Experience Index questions yet."
      />
    );
  }

  if (isEmptyFocus) {
    return (
      <div className="flex flex-col gap-6" data-testid="career-plan-ei-answers-tab">
        <EmptyState
          title="This Competency Isn't Available"
          iconName="quiz"
          caption="These questions aren't in the current Experience Index."
        />
      </div>
    );
  }

  const displayedSelections = isEditing ? draftSelections : catalogSelections;
  const isDirty = isEditing && !experienceIndexSelectionsEqual(draftSelections, catalogSelections);
  const saveBlocked = liveCatalogSelectionBlocked(focusedCatalog, draftSelections);

  function selectedOptionId(questionId: string) {
    return displayedSelections[questionId];
  }

  function selectOption(questionId: string, optionId: string) {
    if (!isEditing || isSaving) return;
    setDraftSelections((current) => ({ ...current, [questionId]: optionId }));
  }

  const headingActions = isEditing
    ? [
        {
          label: "Save",
          variant: "filled" as const,
          testId: "career-plan-ei-answers-save",
          disabled: isSaving || !isDirty || saveBlocked,
          onClick: async () => {
            if (!onSave || !focusedCompetencyId || isSaving || !isDirty || saveBlocked) return;
            const saved = await onSave(
              focusedCompetencyId,
              competencyAnswersPayload(focusedCompetencyId, focusedCatalog, draftSelections)
            );
            if (saved) {
              actionFocusRef.current = "edit";
              setDraftSelections({});
              setIsEditing(false);
            }
          }
        },
        {
          label: "Cancel",
          variant: "text" as const,
          testId: "career-plan-ei-answers-cancel",
          disabled: isSaving,
          onClick: () => {
            if (isSaving) return;
            actionFocusRef.current = "edit";
            setDraftSelections({});
            setIsEditing(false);
          }
        }
      ]
    : showEdit
      ? [
          {
            label: "Edit",
            variant: "outlined" as const,
            testId: "career-plan-ei-answers-edit",
            onClick: () => {
              setDraftSelections({ ...catalogSelections });
              firstRadioOptionIdRef.current = groups[0]?.questions[0]?.options[0]?.optionId ?? null;
              actionFocusRef.current = "first-radio";
              setIsEditing(true);
            }
          }
        ]
      : [];

  return (
    <div
      className="flex flex-col gap-6"
      data-testid="career-plan-ei-answers-tab"
      data-focused={isFocused ? "true" : undefined}
      data-editing={isEditing ? "true" : undefined}
    >
      <SectionHeading
        heading={isFocused && focusedCompetencyName ? focusedCompetencyName : "Experience Index Answers"}
        body={experienceIndexHeadingBody({
          isEditing,
          isFocused,
          showEdit,
          hasSelections: Object.keys(catalogSelections).length > 0
        })}
        actions={headingActions}
      />
      {groups.map((group) => (
        <div
          key={group.competencyId}
          className="flex flex-col gap-6"
          data-testid={`career-plan-ei-answers-${group.competencyId}`}
        >
          {isFocused ? null : (
            <p className="text-title-medium-bold font-serif text-sys-brand-on-surface">{group.competencyName}</p>
          )}
          <ol className="ml-4 list-decimal space-y-4 pl-5">
            {group.questions.map((question) => {
              const questionLabelId = `career-plan-ei-question-label-${question.questionId}`;
              const selectedId = selectedOptionId(question.questionId);

              return (
                <li
                  key={question.questionId}
                  className="text-title-medium"
                  data-testid={`career-plan-ei-question-${question.questionId}`}
                >
                  <div id={questionLabelId}>{question.questionContent}</div>
                  <div role="radiogroup" aria-labelledby={questionLabelId}>
                    {question.options.map((option) => {
                      const selected = selectedId === option.optionId;
                      const optionLabelId = `career-plan-ei-option-label-${option.optionId}`;

                      return (
                        <div
                          key={option.optionId}
                          className="flex items-center my-4 z-0"
                          data-testid={`career-plan-ei-option-${option.optionId}`}
                          data-selected={selected ? "true" : undefined}
                        >
                          <Radio
                            label=""
                            checked={selected}
                            name={`career-plan-ei-question-${question.questionId}`}
                            id={`career-plan-ei-option-${option.optionId}`}
                            disabled={!isEditing || isSaving}
                            aria-labelledby={optionLabelId}
                            onChange={() => selectOption(question.questionId, option.optionId)}
                          />
                          <label
                            htmlFor={`career-plan-ei-option-${option.optionId}`}
                            id={optionLabelId}
                            className={`text-body-large${isEditing && !isSaving ? " cursor-pointer" : ""}`}
                          >
                            {option.optionContent}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mb-10" />
                </li>
              );
            })}
          </ol>
        </div>
      ))}
    </div>
  );
}
